import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { Download, ArrowRight, Users, BookOpen, Award, Calendar, BarChart3 } from 'lucide-react';
import { Spinner } from '../ui/Spinner';
import { Button } from '../ui/Button';
import { PageHeader } from '../ui/PageHeader';
import { toast } from '../ui/Toaster';
import { useNavigation } from '../../contexts/NavigationContext';

interface Stats {
  totalStudents: number;
  activeCourses: number;
  totalCourses: number;
  totalModules: number;
  avgAttendance: number;       // % real contra módulos pasados × alumnos inscritos
  avgGrade: number;            // promedio de course_evaluations.final_average (>0)
  activeEnrollments: number;
  // Asistencia agrupada por alumno
  perfectAttendance: number;   // alumnos sin ninguna ausencia en módulos pasados
  withAbsences: number;        // alumnos con al menos 1 ausencia
  // Distribución de notas (sobre course_evaluations.final_average)
  gradeExcelente: number;      // ≥ 6.0
  gradeBueno: number;          // 5.0 - 5.9
  gradeRegular: number;        // 4.0 - 4.9
  gradeInsuficiente: number;   // < 4.0 y > 0
  studentsWithGrades: number;  // alumnos con promedio registrado
}

export function Reports() {
  const { navigateTo } = useNavigation();
  const [stats, setStats] = useState<Stats>({
    totalStudents: 0,
    activeCourses: 0,
    totalCourses: 0,
    totalModules: 0,
    avgAttendance: 0,
    avgGrade: 0,
    activeEnrollments: 0,
    perfectAttendance: 0,
    withAbsences: 0,
    gradeExcelente: 0,
    gradeBueno: 0,
    gradeRegular: 0,
    gradeInsuficiente: 0,
    studentsWithGrades: 0,
  });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const nowMs = Date.now();
      const [students, courses, modules, attendance, evaluations, enrollments] = await Promise.all([
        supabase.from('profiles').select('id').eq('role', 'alumno').eq('active', true),
        supabase.from('courses').select('id, status'),
        // Necesitamos course_id + end_date para saber qué módulos ya pasaron
        supabase.from('modules').select('id, course_id, end_date'),
        // Solo se insertan registros con present=true (la app no escribe ausencias).
        // Calculamos asistencia esperada = enrollments × módulos pasados.
        supabase.from('attendance').select('module_id, alumno_id, present'),
        // Promedios reales: viven en course_evaluations.final_average
        supabase.from('course_evaluations').select('alumno_id, final_average'),
        supabase.from('course_enrollments').select('course_id, alumno_id').eq('status', 'active'),
      ]);

      const coursesData = courses.data ?? [];
      const studentsRows = students.data ?? [];
      const modulesData = modules.data ?? [];
      const enrollmentsData = enrollments.data ?? [];
      const attendanceRecords = attendance.data ?? [];

      // ─── Asistencia bien calculada ────────────────────────────────
      // Para cada módulo pasado: el "esperado" es la cantidad de alumnos
      // inscritos en ese curso. El "real" es cuántos tienen present=true.
      const enrollmentsByCourse = new Map<string, string[]>();
      for (const e of enrollmentsData) {
        const arr = enrollmentsByCourse.get(e.course_id) ?? [];
        arr.push(e.alumno_id);
        enrollmentsByCourse.set(e.course_id, arr);
      }
      const presentSet = new Set<string>(); // "moduleId|alumnoId"
      for (const a of attendanceRecords) {
        if (a.present) presentSet.add(`${a.module_id}|${a.alumno_id}`);
      }
      // Por alumno: contamos esperadas vs presentes para derivar perfecta/con inasistencias
      const expectedByStudent = new Map<string, number>();
      const attendedByStudent = new Map<string, number>();
      let totalExpected = 0;
      let totalAttended  = 0;
      for (const m of modulesData) {
        if (!m.end_date) continue;
        const endMs = new Date(m.end_date).getTime();
        if (Number.isNaN(endMs) || endMs > nowMs) continue; // módulo no pasó aún
        const enrolled = enrollmentsByCourse.get(m.course_id) ?? [];
        for (const alumnoId of enrolled) {
          totalExpected++;
          expectedByStudent.set(alumnoId, (expectedByStudent.get(alumnoId) ?? 0) + 1);
          if (presentSet.has(`${m.id}|${alumnoId}`)) {
            totalAttended++;
            attendedByStudent.set(alumnoId, (attendedByStudent.get(alumnoId) ?? 0) + 1);
          }
        }
      }
      const avgAttendance = totalExpected > 0 ? (totalAttended / totalExpected) * 100 : 0;

      // Conteo real de "asistencia perfecta" (sin ausencias en módulos pasados)
      // Solo consideramos alumnos que tuvieron al menos 1 módulo pasado.
      let perfectAttendance = 0, withAbsences = 0;
      for (const [alumnoId, expected] of expectedByStudent) {
        if (expected === 0) continue;
        const attended = attendedByStudent.get(alumnoId) ?? 0;
        if (attended >= expected) perfectAttendance++;
        else                      withAbsences++;
      }

      // ─── Promedio académico (de course_evaluations.final_average) ──
      // Sacamos un promedio por alumno (puede tener varios cursos).
      const evals = evaluations.data ?? [];
      const finalsByStudent = new Map<string, number[]>();
      for (const ev of evals) {
        const avg = typeof ev.final_average === 'string' ? parseFloat(ev.final_average) : ev.final_average;
        if (!avg || avg <= 0) continue;
        const arr = finalsByStudent.get(ev.alumno_id) ?? [];
        arr.push(avg);
        finalsByStudent.set(ev.alumno_id, arr);
      }
      const promediosPorAlumno: number[] = [];
      for (const arr of finalsByStudent.values()) {
        promediosPorAlumno.push(arr.reduce((a, b) => a + b, 0) / arr.length);
      }
      const avgGrade = promediosPorAlumno.length
        ? promediosPorAlumno.reduce((a, b) => a + b, 0) / promediosPorAlumno.length
        : 0;

      // Distribución (escala chilena 1-7)
      let gradeExcelente = 0, gradeBueno = 0, gradeRegular = 0, gradeInsuficiente = 0;
      for (const g of promediosPorAlumno) {
        if      (g >= 6.0) gradeExcelente++;
        else if (g >= 5.0) gradeBueno++;
        else if (g >= 4.0) gradeRegular++;
        else if (g  > 0)   gradeInsuficiente++;
      }

      setStats({
        totalStudents: studentsRows.length,
        totalCourses:  coursesData.length,
        activeCourses: coursesData.filter((c) => c.status === 'active').length,
        totalModules:  modulesData.length,
        avgAttendance,
        avgGrade,
        activeEnrollments: enrollmentsData.length,
        perfectAttendance,
        withAbsences,
        gradeExcelente,
        gradeBueno,
        gradeRegular,
        gradeInsuficiente,
        studentsWithGrades: promediosPorAlumno.length,
      });
    } catch (error) {
      console.error('Error loading stats:', error);
      setLoadError(true);
      toast.error('No se pudieron cargar los reportes');
    } finally {
      setLoading(false);
    }
  };

  const exportReport = async () => {
    setExporting(true);
    try {
      const rows = [
        ['Métrica', 'Valor'],
        ['Total alumnos activos', stats.totalStudents],
        ['Cursos activos', stats.activeCourses],
        ['Cursos totales', stats.totalCourses],
        ['Módulos totales', stats.totalModules],
        ['Inscripciones activas', stats.activeEnrollments],
        ['Asistencia promedio (%)', stats.avgAttendance.toFixed(1)],
        ['Asistencia perfecta (alumnos)', stats.perfectAttendance],
        ['Con inasistencias (alumnos)', stats.withAbsences],
        ['Promedio académico (escala 1-7)', stats.avgGrade.toFixed(2)],
        ['Alumnos con promedio registrado', stats.studentsWithGrades],
        ['Excelente (≥6.0)', stats.gradeExcelente],
        ['Bueno (5.0–5.9)', stats.gradeBueno],
        ['Regular (4.0–4.9)', stats.gradeRegular],
        ['Insuficiente (<4.0)', stats.gradeInsuficiente],
      ];
      const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
      const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const fecha = new Date().toISOString().split('T')[0];
      a.download = `reporte-cubus-${fecha}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Reporte exportado');
    } catch (err) {
      console.error(err);
      toast.error('No se pudo exportar el reporte');
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="glass-surface rounded-xl p-8 text-center border border-red-500/20">
        <p className="text-white font-semibold mb-2">No pudimos cargar los reportes</p>
        <p className="text-muted mb-4 text-sm">Verifica tu conexión e intenta nuevamente.</p>
        <Button variant="lime" onClick={loadStats}>Reintentar</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 sm:p-6 lg:p-8 overflow-y-auto">
      <PageHeader
        eyebrow="Analítica"
        eyebrowIcon={BarChart3}
        title="Reportes y Estadísticas"
        description="Visión consolidada del rendimiento académico, asistencia y adopción de la plataforma."
        actions={
          <Button variant="lime" leftIcon={<Download className="w-4 h-4" />} onClick={exportReport} loading={exporting}>
            Exportar CSV
          </Button>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <button
          onClick={() => navigateTo('users')}
          className="text-left glass-surface rounded-2xl p-6 glass-hover border border-white/[0.06] hover:border-blue-400/40 transition-all group focus-lime"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 rounded-xl bg-blue-500/15 flex items-center justify-center">
              <Users className="w-6 h-6 text-blue-300" aria-hidden="true" />
            </div>
            <ArrowRight className="w-5 h-5 text-white/30 group-hover:text-blue-300 group-hover:translate-x-0.5 transition-all" aria-hidden="true" />
          </div>
          <h3 className="text-subtle text-sm mb-1">Total alumnos</h3>
          <p className="text-3xl font-bold text-white text-display">{stats.totalStudents}</p>
          <p className="text-sm text-emerald-400 mt-2">Activos en la plataforma</p>
        </button>

        <button
          onClick={() => navigateTo('courses')}
          className="text-left glass-surface rounded-2xl p-6 glass-hover border border-white/[0.06] hover:border-cubus-violet/40 transition-all group focus-lime"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 rounded-xl bg-cubus-violet/15 flex items-center justify-center">
              <BookOpen className="w-6 h-6 text-cubus-violet" aria-hidden="true" />
            </div>
            <ArrowRight className="w-5 h-5 text-white/30 group-hover:text-cubus-violet group-hover:translate-x-0.5 transition-all" aria-hidden="true" />
          </div>
          <h3 className="text-subtle text-sm mb-1">Cursos activos</h3>
          <p className="text-3xl font-bold text-white text-display">{stats.activeCourses}</p>
          <p className="text-sm text-cubus-violet mt-2">
            {stats.totalCourses} totales · {stats.totalModules} módulos
          </p>
        </button>

        <button
          onClick={() => navigateTo('users')}
          className="text-left glass-surface rounded-2xl p-6 glass-hover border border-white/[0.06] hover:border-cubus-lime/40 transition-all group focus-lime"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 rounded-xl bg-cubus-lime/15 flex items-center justify-center">
              <Calendar className="w-6 h-6 text-cubus-lime" aria-hidden="true" />
            </div>
            <ArrowRight className="w-5 h-5 text-white/30 group-hover:text-cubus-lime group-hover:translate-x-0.5 transition-all" aria-hidden="true" />
          </div>
          <h3 className="text-subtle text-sm mb-1">Inscripciones</h3>
          <p className="text-3xl font-bold text-white text-display">{stats.activeEnrollments}</p>
          <p className="text-sm text-cubus-lime mt-2">
            {stats.totalStudents > 0
              ? `~${(stats.activeEnrollments / Math.max(1, stats.totalStudents)).toFixed(1)} cursos por alumno`
              : 'Estudiantes inscritos'}
          </p>
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="glass-surface rounded-2xl p-6">
          <h2 className="text-xl font-semibold text-white text-display mb-6">Promedio de asistencia</h2>
          <div className="relative pt-1">
            <div className="flex mb-2 items-center justify-between">
              <span className="chip chip-emerald uppercase">Asistencia general</span>
              <span className="text-sm font-semibold text-emerald-400">
                {stats.avgAttendance.toFixed(1)}%
              </span>
            </div>
            <div
              role="progressbar"
              aria-label="Asistencia general"
              aria-valuenow={Math.round(stats.avgAttendance)}
              aria-valuemin={0}
              aria-valuemax={100}
              className="overflow-hidden h-3 rounded-full bg-white/[0.05] border border-white/[0.06]"
            >
              <div
                style={{ width: `${stats.avgAttendance}%` }}
                className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400 transition-all duration-700"
              />
            </div>
          </div>

          <div className="mt-6 space-y-3">
            <div className="flex justify-between items-center p-3 bg-white/[0.03] border border-white/[0.06] rounded-lg">
              <span className="text-muted">Asistencia perfecta</span>
              <span className="text-cubus-lime font-semibold">{stats.perfectAttendance}</span>
            </div>
            <div className="flex justify-between items-center p-3 bg-white/[0.03] border border-white/[0.06] rounded-lg">
              <span className="text-muted">Con inasistencias</span>
              <span className="text-red-400 font-semibold">{stats.withAbsences}</span>
            </div>
            {stats.perfectAttendance + stats.withAbsences === 0 && (
              <p className="text-xs text-faint italic px-1">
                Sin módulos con fecha pasada todavía — los datos aparecerán cuando termine la primera clase.
              </p>
            )}
          </div>
        </div>

        <div className="glass-surface rounded-2xl p-6">
          <h2 className="text-xl font-semibold text-white text-display mb-6">Rendimiento académico</h2>
          <div className="flex items-center justify-center mb-6">
            <div className="relative w-40 h-40">
              {/* Escala chilena 1-7: el círculo se llena en proporción a 7 */}
              <svg className="w-full h-full transform -rotate-90" role="img" aria-label={`Promedio ${stats.avgGrade.toFixed(1)} de 7`}>
                <circle cx="80" cy="80" r="70" stroke="rgba(255,255,255,0.08)" strokeWidth="12" fill="none" />
                <circle
                  cx="80"
                  cy="80"
                  r="70"
                  stroke="#DDFF00"
                  strokeWidth="12"
                  fill="none"
                  strokeLinecap="round"
                  strokeDasharray={`${Math.min(stats.avgGrade / 7, 1) * 440} 440`}
                  style={{ filter: 'drop-shadow(0 0 6px rgba(221,255,0,0.6))' }}
                  className="transition-all duration-1000"
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center">
                  <div className="text-4xl font-bold text-white text-display">{stats.avgGrade.toFixed(1)}</div>
                  <div className="text-sm text-muted">de 7.0</div>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            {[
              { label: 'Excelente (≥6.0)',     count: stats.gradeExcelente,     tone: 'text-emerald-400' },
              { label: 'Bueno (5.0–5.9)',      count: stats.gradeBueno,         tone: 'text-cubus-cyan' },
              { label: 'Regular (4.0–4.9)',    count: stats.gradeRegular,       tone: 'text-amber-400' },
              { label: 'Insuficiente (<4.0)',  count: stats.gradeInsuficiente,  tone: 'text-rose-400' },
            ].map((row) => (
              <div
                key={row.label}
                className="flex justify-between items-center p-3 bg-white/[0.03] border border-white/[0.06] rounded-lg"
              >
                <div className="flex items-center gap-2">
                  <Award className={`w-4 h-4 ${row.tone}`} aria-hidden="true" />
                  <span className="text-muted text-sm">{row.label}</span>
                </div>
                <span className={`font-semibold ${row.count > 0 ? row.tone : 'text-white/30'}`}>
                  {row.count}
                </span>
              </div>
            ))}
            {stats.studentsWithGrades === 0 && (
              <p className="text-xs text-faint italic px-1 mt-2">
                Aún no hay promedios registrados — aparecerán cuando los docentes publiquen entregas con nota.
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="glass-surface rounded-2xl p-6">
        <h2 className="text-xl font-semibold text-white text-display mb-4">Resumen ejecutivo</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h3 className="text-sm font-semibold text-subtle mb-3 uppercase tracking-wide">Indicadores clave</h3>
            <ul className="space-y-2 text-muted">
              <li className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-emerald-500" />
                Tasa de asistencia ({stats.avgAttendance.toFixed(1)}%)
              </li>
              <li className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-cubus-lime" />
                Promedio académico ({stats.avgGrade.toFixed(1)})
              </li>
              <li className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-blue-500" />
                {stats.activeEnrollments} inscripciones activas
              </li>
            </ul>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-subtle mb-3 uppercase tracking-wide">Recomendaciones</h3>
            <ul className="space-y-2 text-muted">
              <li className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-amber-500" />
                Implementar tutorías para alumnos con bajo rendimiento
              </li>
              <li className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-cubus-violet" />
                Aumentar recursos en biblioteca digital
              </li>
              <li className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-red-500" />
                Monitorear ausentismo recurrente
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
